<template>
  <div class="team-audit-container">
    <div class="audit-header">
      <h2 class="audit-title">
        {{ $t("members.team_audit.title") }}
      </h2>
      <div class="audit-summary">
        <span class="summary-item">
          <strong>{{ totalTeams }}</strong> {{ $t("members.team_audit.teams_analyzed") }}
        </span>
        <span class="summary-item" :class="{ 'has-issues': discrepancies.length > 0 }">
          <strong>{{ discrepancies.length }}</strong> {{ $t("members.team_audit.discrepancies_found") }}
        </span>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading"></i>
      <span>{{ $t("members.team_audit.analyzing") }}</span>
    </div>

    <div v-else-if="discrepancies.length === 0" class="no-issues">
      <i class="el-icon-success"></i>
      <p>{{ $t("members.team_audit.all_valid") }}</p>
    </div>

    <div v-else class="discrepancies-table">
             <div class="audit-cards-container">
         <div 
           v-for="discrepancy in discrepancies" 
           :key="discrepancy.teamId"
           class="audit-card"
         >
           <!-- Team Header -->
           <div class="audit-card-header">
             <div class="team-info-main">
               <div
                 class="team-color-indicator"
                 :style="{ backgroundColor: discrepancy.teamColor || '#747474' }"
               ></div>
               <h3 class="team-name-main">{{ discrepancy.teamName }}</h3>
             </div>
             <el-button
               v-if="discrepancy.canFix"
               type="primary"
               size="small"
               @click="fixRelationship(discrepancy)"
               :loading="discrepancy.fixing"
               class="fix-button"
             >
               {{ $t("members.team_audit.fix") }}
             </el-button>
             <el-tooltip
               v-else
               :content="$t('members.team_audit.manual_review_tooltip')"
               placement="top"
             >
               <span class="manual-review-badge">{{ $t("members.team_audit.manual_review") }}</span>
             </el-tooltip>
           </div>

           <!-- Content Grid -->
           <div class="audit-card-content">
             <!-- Current Manager -->
             <div class="info-section">
               <div class="section-label">{{ $t("members.team_audit.team_manager") }}</div>
               <div class="person-info">
                 <div v-if="discrepancy.hasMultipleManagers" class="person-detail">
                   <img
                     :src="$utils('icons', 'ic_multiple_managers')"
                     alt="Multiple managers"
                     class="person-avatar"
                   />
                   <span class="person-name">{{ $t("members.team_audit.multiple_managers") }}</span>
                 </div>
                 <div v-else-if="discrepancy.managerName" class="person-detail">
                   <img
                     v-if="discrepancy.managerPic"
                     :src="discrepancy.managerPic"
                     class="person-avatar"
                   />
                   <div v-else class="person-avatar person-avatar-placeholder">
                     {{ getInitials(discrepancy.managerName) }}
                   </div>
                   <span class="person-name">{{ discrepancy.managerName }}</span>
                 </div>
                 <div v-else class="person-detail">
                   <div class="person-avatar person-avatar-placeholder">?</div>
                   <span class="person-name unassigned">{{ $t("members.team_audit.unassigned") }}</span>
                 </div>
               </div>
             </div>

             <!-- Manager's Boss -->
             <div class="info-section">
               <div class="section-label">{{ $t("members.team_audit.managers_boss") }}</div>
               <div class="person-info">
                 <div v-if="discrepancy.managerBossName" class="person-detail">
                   <img
                     v-if="discrepancy.managerBossPic"
                     :src="discrepancy.managerBossPic"
                     class="person-avatar"
                   />
                   <div v-else class="person-avatar person-avatar-placeholder">
                     {{ getInitials(discrepancy.managerBossName) }}
                   </div>
                   <div class="person-details">
                     <span class="person-name">{{ discrepancy.managerBossName }}</span>
                     <div v-if="discrepancy.managerBossTeamName" class="person-team">
                       <div
                         class="team-indicator"
                         :style="{ backgroundColor: discrepancy.managerBossTeamColor || '#747474' }"
                       ></div>
                       <span class="team-name-text">{{ discrepancy.managerBossTeamName }}</span>
                     </div>
                   </div>
                 </div>
                 <div v-else class="person-detail">
                   <div class="person-avatar person-avatar-placeholder">?</div>
                   <span class="person-name unassigned">{{ $t("members.team_audit.no_boss_top_level") }}</span>
                 </div>
               </div>
             </div>

             <!-- Hierarchy Visualization -->
             <div class="info-section hierarchy-section">
               <div class="section-label">{{ $t("members.team_audit.team_hierarchy") }}</div>
               <div class="hierarchy-comparison">
                 <!-- Current Hierarchy -->
                 <div class="hierarchy-column">
                   <div class="hierarchy-label current">{{ $t("members.team_audit.current_structure") }}</div>
                   <div class="hierarchy-tree">
                     <div v-if="discrepancy.currentHierarchy" class="tree-structure">
                       <div 
                         v-for="(level, index) in discrepancy.currentHierarchy" 
                         :key="'current-' + index"
                         class="tree-level"
                       >
                         <div 
                           class="tree-node"
                           :class="{ 
                             'highlight': level.isTarget,
                             'parent': level.isParent,
                             'child': level.isChild
                           }"
                         >
                           <div
                             class="tree-node-color"
                             :style="{ backgroundColor: level.color || '#747474' }"
                           ></div>
                           <span class="tree-node-name">{{ level.name }}</span>
                         </div>
                         <div v-if="index < discrepancy.currentHierarchy.length - 1" class="tree-connector">│</div>
                       </div>
                     </div>
                     <div v-else class="no-hierarchy">
                       <div class="tree-node highlight">
                         <div
                           class="tree-node-color"
                           :style="{ backgroundColor: discrepancy.teamColor || '#747474' }"
                         ></div>
                         <span class="tree-node-name">{{ discrepancy.teamName }}</span>
                       </div>
                       <div class="tree-note">{{ $t("members.team_audit.no_parent_team") }}</div>
                     </div>
                   </div>
                 </div>

                 <!-- Expected Hierarchy -->
                 <div class="hierarchy-column">
                   <div class="hierarchy-label expected">{{ $t("members.team_audit.recommended") }}</div>
                   <div class="hierarchy-tree">
                     <div v-if="discrepancy.expectedHierarchy" class="tree-structure">
                       <div 
                         v-for="(level, index) in discrepancy.expectedHierarchy" 
                         :key="'expected-' + index"
                         class="tree-level"
                       >
                         <div 
                           class="tree-node"
                           :class="{ 
                             'highlight': level.isTarget,
                             'parent': level.isParent,
                             'child': level.isChild,
                             'new-parent': level.isNewParent
                           }"
                         >
                           <div
                             class="tree-node-color"
                             :style="{ backgroundColor: level.color || '#747474' }"
                           ></div>
                           <span class="tree-node-name">{{ level.name }}</span>
                           <span v-if="level.isNewParent" class="new-badge">{{ $t("members.team_audit.new_badge") }}</span>
                         </div>
                         <div v-if="index < discrepancy.expectedHierarchy.length - 1" class="tree-connector">│</div>
                       </div>
                     </div>
                     <div v-else class="no-hierarchy">
                       <div class="tree-node highlight">
                         <div
                           class="tree-node-color"
                           :style="{ backgroundColor: discrepancy.teamColor || '#747474' }"
                         ></div>
                         <span class="tree-node-name">{{ discrepancy.teamName }}</span>
                       </div>
                       <div class="tree-note">{{ $t("members.team_audit.should_have_no_parent") }}</div>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           </div>
                  </div>
       </div>

      <div class="audit-actions">
        <el-button
          type="primary"
          @click="fixAllRelationships"
          :disabled="!hasFixableIssues"
          :loading="fixingAll"
        >
          {{ $t("members.team_audit.fix_all_issues") }} ({{ fixableCount }})
        </el-button>
        <el-button @click="runAudit">
          {{ $t("members.team_audit.rerun_audit") }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "firebase/app";
import "firebase/database";
import format from "date-fns/format";
import _ from "lodash";

export default {
  name: "team-audit",
  data() {
    return {
      loading: false,
      discrepancies: [],
      fixingAll: false,
    };
  },
  computed: {
    user() {
      return this.$store.getters["authen/user"];
    },
    totalTeams() {
      return this.$store.getters["newteam/teamsall"].length;
    },
    hasFixableIssues() {
      return this.discrepancies.some(d => d.canFix);
    },
    fixableCount() {
      return this.discrepancies.filter(d => d.canFix).length;
    },
  },
  mounted() {
    this.runAudit();
  },
  methods: {
    async runAudit() {
      this.loading = true;
      this.discrepancies = [];

      try {
        // Get all teams
        const teamsSnapshot = await firebase
          .database()
          .ref()
          .child("team")
          .orderByChild("company")
          .equalTo(this.user.company)
          .once("value");

        // Get all users
        const usersSnapshot = await firebase
          .database()
          .ref()
          .child("users")
          .orderByChild("company")
          .equalTo(this.user.company)
          .once("value");

        const teams = {};
        const users = {};

        teamsSnapshot.forEach((doc) => {
          const team = doc.val();
          team.key = doc.key;
          teams[doc.key] = team;
        });

        usersSnapshot.forEach((doc) => {
          const user = doc.val();
          user.key = doc.key;
          users[doc.key] = user;
        });

        // Analyze each team
        for (const teamId in teams) {
          const team = teams[teamId];
          const discrepancy = await this.analyzeTeam(team, teams, users);
          
          if (discrepancy) {
            this.discrepancies.push(discrepancy);
          }
        }

      } catch (error) {
        console.error("Error running audit:", error);
        this.$message.error(this.$t("members.team_audit.failed_run"));
      } finally {
        this.loading = false;
      }
    },

    analyzeTeam(team, allTeams, allUsers) {
      // Find team manager(s)
      const teamManagers = this.findTeamManagers(team, allUsers);
      
      if (teamManagers.length === 0) {
        // No manager, can't determine parent
        return null;
      }

      // Handle multiple managers
      if (teamManagers.length > 1) {
        // Check if all managers have the same boss
        const bosses = teamManagers
          .map(m => m.boss)
          .filter(b => b);

        if (bosses.length === 0) {
          // No managers have bosses
          return null;
        }

        const uniqueBosses = _.uniq(bosses);
        if (uniqueBosses.length > 1) {
          // Multiple different bosses - needs manual review
                     return {
             teamId: team.key,
             teamName: team.name,
             teamColor: team.color,
             hasMultipleManagers: true,
             currentParentId: team.parent,
             currentParentName: team.parent && allTeams[team.parent] ? allTeams[team.parent].name : null,
             currentParentColor: team.parent && allTeams[team.parent] ? allTeams[team.parent].color : null,
             canFix: false,
             reason: "Multiple managers with different bosses"
           };
        }

        // All managers have the same boss
        const bossEmail = uniqueBosses[0];
        return this.checkSingleManager(team, { boss: bossEmail }, allTeams, allUsers);
      }

      // Single manager
      const manager = teamManagers[0];
      return this.checkSingleManager(team, manager, allTeams, allUsers);
    },

    findTeamManagers(team, allUsers) {
      const managers = [];
      
      // Look for users who are marked as boss of this team
      for (const userId in allUsers) {
        const user = allUsers[userId];
        if (user.boss_of && user.boss_of.includes(team.key)) {
          managers.push({
            ...user,
            email: user.email || userId.replace(/\|\|/g, ".")
          });
        }
      }

      // If no explicit boss_of, look at team members and find who doesn't have a boss within the team
      if (managers.length === 0) {
        const teamMembers = [];
        for (const userId in allUsers) {
          const user = allUsers[userId];
          if (user.team === team.key) {
            teamMembers.push({
              ...user,
              email: user.email || userId.replace(/\|\|/g, ".")
            });
          }
        }

        // Find members whose boss is not in the same team
        for (const member of teamMembers) {
          if (!member.boss) {
            // No boss - could be top manager
            managers.push(member);
          } else {
            const bossKey = member.boss.replace(/\./g, "||");
            const boss = allUsers[bossKey];
            if (boss && boss.team !== team.key) {
              // Boss is in different team - this person manages this team
              managers.push(member);
            }
          }
        }
      }

      return managers;
    },

         checkSingleManager(team, manager, allTeams, allUsers) {
       if (!manager.boss) {
         // Manager has no boss - this team should have no parent
         if (team.parent) {
           const discrepancy = {
             teamId: team.key,
             teamName: team.name,
             teamColor: team.color,
             managerName: this.formatName(manager),
             managerPic: manager.pic,
             currentParentId: team.parent,
             currentParentName: team.parent && allTeams[team.parent] ? allTeams[team.parent].name : null,
             currentParentColor: team.parent && allTeams[team.parent] ? allTeams[team.parent].color : null,
             expectedParentId: null,
             expectedParentName: null,
             canFix: true,
             fixing: false
           };
           
           // Build hierarchy trees
           discrepancy.currentHierarchy = this.buildHierarchyTree(team, allTeams, 'current');
           discrepancy.expectedHierarchy = null; // No parent expected
           
           return discrepancy;
         }
         return null;
       }

       // Get manager's boss
       const bossKey = manager.boss.replace(/\./g, "||");
       const managerBoss = allUsers[bossKey];
       
       if (!managerBoss) {
         // Boss not found
         return null;
       }

       const expectedParentTeamId = managerBoss.team;
       const managerBossTeam = expectedParentTeamId ? allTeams[expectedParentTeamId] : null;
       
       // Check if it's trying to set itself as parent
       if (expectedParentTeamId === team.key) {
         const discrepancy = {
           teamId: team.key,
           teamName: team.name,
           teamColor: team.color,
           managerName: this.formatName(manager),
           managerPic: manager.pic,
           managerBossName: this.formatName(managerBoss),
           managerBossPic: managerBoss.pic,
           managerBossTeamName: managerBossTeam ? managerBossTeam.name : null,
           managerBossTeamColor: managerBossTeam ? managerBossTeam.color : null,
           currentParentId: team.parent,
           currentParentName: team.parent && allTeams[team.parent] ? allTeams[team.parent].name : null,
           currentParentColor: team.parent && allTeams[team.parent] ? allTeams[team.parent].color : null,
           expectedParentId: null,
           expectedParentName: null,
           canFix: false,
           reason: "Manager's boss is on the same team - would create circular reference"
         };
         
         // Build hierarchy trees
         discrepancy.currentHierarchy = this.buildHierarchyTree(team, allTeams, 'current');
         discrepancy.expectedHierarchy = null;
         
         return discrepancy;
       }

       // Check if current parent matches expected parent
       if (team.parent !== expectedParentTeamId) {
         const discrepancy = {
           teamId: team.key,
           teamName: team.name,
           teamColor: team.color,
           managerName: this.formatName(manager),
           managerPic: manager.pic,
           managerBossName: this.formatName(managerBoss),
           managerBossPic: managerBoss.pic,
           managerBossTeamName: managerBossTeam ? managerBossTeam.name : null,
           managerBossTeamColor: managerBossTeam ? managerBossTeam.color : null,
           currentParentId: team.parent,
           currentParentName: team.parent && allTeams[team.parent] ? allTeams[team.parent].name : null,
           currentParentColor: team.parent && allTeams[team.parent] ? allTeams[team.parent].color : null,
           expectedParentId: expectedParentTeamId,
           expectedParentName: expectedParentTeamId && allTeams[expectedParentTeamId] ? allTeams[expectedParentTeamId].name : null,
           expectedParentColor: expectedParentTeamId && allTeams[expectedParentTeamId] ? allTeams[expectedParentTeamId].color : null,
           canFix: true,
           fixing: false
         };
         
         // Build hierarchy trees
         discrepancy.currentHierarchy = this.buildHierarchyTree(team, allTeams, 'current');
         discrepancy.expectedHierarchy = this.buildHierarchyTree(team, allTeams, 'expected', expectedParentTeamId);
         
         return discrepancy;
       }

       return null;
     },

     buildHierarchyTree(targetTeam, allTeams, type, expectedParentId = null) {
       const hierarchy = [];
       
       // Determine the parent team ID based on type
       let parentId;
       if (type === 'current') {
         parentId = targetTeam.parent;
       } else if (type === 'expected') {
         parentId = expectedParentId;
       }
       
       // Build upward hierarchy (grandparent -> parent)
       const upwardChain = [];
       let currentParentId = parentId;
       let depth = 0;
       const maxDepth = 3; // Prevent infinite loops
       
       while (currentParentId && depth < maxDepth) {
         const parentTeam = allTeams[currentParentId];
         if (!parentTeam) break;
         
         upwardChain.unshift({
           id: currentParentId,
           name: parentTeam.name,
           color: parentTeam.color,
           isParent: true,
           isTarget: false,
           isChild: false,
           isNewParent: type === 'expected' && currentParentId === expectedParentId
         });
         
         currentParentId = parentTeam.parent;
         depth++;
       }
       
       // Add the upward chain to hierarchy
       hierarchy.push(...upwardChain);
       
       // Add the target team
       hierarchy.push({
         id: targetTeam.key,
         name: targetTeam.name,
         color: targetTeam.color,
         isParent: false,
         isTarget: true,
         isChild: false,
         isNewParent: false
       });
       
       // Find and add child teams (teams that have this team as parent)
       const childTeams = Object.values(allTeams).filter(team => 
         team.parent === targetTeam.key
       ).slice(0, 2); // Limit to 2 children for display
       
       childTeams.forEach(childTeam => {
         hierarchy.push({
           id: childTeam.key,
           name: childTeam.name,
           color: childTeam.color,
           isParent: false,
           isTarget: false,
           isChild: true,
           isNewParent: false
         });
       });
       
       // If there are more children, add an indicator
       const totalChildren = Object.values(allTeams).filter(team => 
         team.parent === targetTeam.key
       ).length;
       
       if (totalChildren > 2) {
         hierarchy.push({
           id: 'more',
           name: `+${totalChildren - 2} more teams`,
           color: '#e0e0e0',
           isParent: false,
           isTarget: false,
           isChild: true,
           isMore: true,
           isNewParent: false
         });
       }
       
       return hierarchy.length > 1 ? hierarchy : null;
     },

    formatName(user) {
      if (!user) return "";
      return `${user.firstname || ""} ${user.lastname || ""}`.trim();
    },

    getInitials(name) {
      if (!name) return "";
      const parts = name.split(" ");
      return parts
        .map(p => p.charAt(0).toUpperCase())
        .join("")
        .slice(0, 2);
    },

    async fixRelationship(discrepancy) {
      discrepancy.fixing = true;
      
      try {
        const updates = {
          parent: discrepancy.expectedParentId || "",
          updated: format(new Date(), "YYYY-MM-DD HH:mm")
        };

        await firebase
          .database()
          .ref(`team/${discrepancy.teamId}`)
          .update(updates);

    

        this.$message.success(`${this.$t("members.team_audit.fixed_relationship_for")} ${discrepancy.teamName}`);
        
        // Remove from discrepancies list
        const index = this.discrepancies.indexOf(discrepancy);
        if (index > -1) {
          this.discrepancies.splice(index, 1);
        }

      } catch (error) {
        console.error("Error fixing relationship:", error);
        this.$message.error(`${this.$t("members.team_audit.failed_fix")} ${discrepancy.teamName}`);
      } finally {
        discrepancy.fixing = false;
      }
    },

    async fixAllRelationships() {
      const fixableDiscrepancies = this.discrepancies.filter(d => d.canFix);
      
      if (fixableDiscrepancies.length === 0) return;

      this.fixingAll = true;

      try {
        const promises = fixableDiscrepancies.map(d => this.fixRelationship(d));
        await Promise.all(promises);
        
        this.$message.success(`${this.$t("members.team_audit.fixed_prefix")} ${fixableDiscrepancies.length} ${this.$t("members.team_audit.fixed_suffix")}`);

      } catch (error) {
        console.error("Error fixing all relationships:", error);
        this.$message.error(this.$t("members.team_audit.some_failed"));
      } finally {
        this.fixingAll = false;
      }
    }
  }
};
</script>

<style scoped>
.team-audit-container {
  padding: 24px;
  background: #ffffff;
  min-height: 400px;
}

.audit-header {
  margin-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 16px;
}

.audit-title {
  font-size: 24px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.audit-summary {
  display: flex;
  gap: 24px;
  font-size: 16px;
  color: #4a4a4a;
}

.summary-item strong {
  color: #000000;
}

.summary-item.has-issues strong {
  color: #f44336;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 16px;
  color: #4a4a4a;
}

.loading-container i {
  font-size: 32px;
  color: #32c2d5;
}

.no-issues {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 16px;
  color: #4a4a4a;
}

.no-issues i {
  font-size: 48px;
  color: #67c23a;
}

.no-issues p {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.audit-cards-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
}

.audit-card {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}

.audit-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.audit-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.team-info-main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.team-color-indicator {
  width: 6px;
  height: 32px;
  border-radius: 3px;
}

.team-name-main {
  font-size: 18px;
  font-weight: 700;
  color: #000000;
  margin: 0;
}

.fix-button {
  background-color: #32c2d5;
  border-color: #32c2d5;
  font-weight: 600;
}

.manual-review-badge {
  background: #ff9800;
  color: #ffffff;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.audit-card-content {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 24px;
  padding: 20px;
}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-label {
  font-size: 12px;
  font-weight: 700;
  color: #666666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.person-info {
  display: flex;
  flex-direction: column;
}

.person-detail {
  display: flex;
  align-items: center;
  gap: 12px;
}

.person-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.person-avatar-placeholder {
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: #4a4a4a;
}

.person-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.person-name {
  font-weight: 600;
  font-size: 15px;
  color: #000000;
}

.person-team {
  display: flex;
  align-items: center;
  gap: 6px;
}

.team-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.team-name-text {
  font-size: 12px;
  color: #666666;
  font-weight: 500;
}

.hierarchy-section {
  border-left: 3px solid #f0f0f0;
  padding-left: 16px;
}

.hierarchy-comparison {
  display: flex;
  gap: 24px;
}

.hierarchy-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hierarchy-label {
  font-size: 12px;
  font-weight: 600;
  /* text-transform: uppercase; */
  letter-spacing: 0.5px;
  padding-bottom: 8px;
  border-bottom: 2px solid transparent;
}

.hierarchy-label.current {
  color: #666666;
  border-bottom-color: #e0e0e0;
}

.hierarchy-label.expected {
  color: #32c2d5;
  border-bottom-color: #32c2d5;
}

.hierarchy-tree {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 120px;
}

.tree-structure {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.tree-level {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.tree-node {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  min-width: 120px;
  justify-content: flex-start;
  position: relative;
  transition: all 0.2s ease;
}

.tree-node.highlight {
  background: #e3f2fd;
  border-color: #2196f3;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(33, 150, 243, 0.2);
}

.tree-node.parent {
  background: #f3e5f5;
  border-color: #9c27b0;
}

.tree-node.child {
  background: #e8f5e8;
  border-color: #4caf50;
  opacity: 0.8;
}

.tree-node.new-parent {
  background: #fff3e0;
  border-color: #ff9800;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(255, 152, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0); }
}

.tree-node-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

.tree-node-name {
  font-size: 12px;
  color: #000000;
  flex: 1;
  text-align: left;
}

.new-badge {
  background: #ff9800;
  color: #ffffff;
  font-size: 8px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 8px;
  position: absolute;
  top: -6px;
  right: -6px;
}

.tree-connector {
  color: #cccccc;
  font-size: 16px;
  font-weight: 300;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-hierarchy {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 0;
}

.tree-note {
  font-size: 11px;
  color: #999999;
  font-style: italic;
  text-align: center;
}

.unassigned {
  color: #9e9e9e;
  font-style: italic;
}

.manual-review {
  color: #ff9800;
  font-size: 12px;
  font-weight: 600;
}

.audit-actions {
  margin-top: 24px;
  display: flex;
  gap: 12px;
}
</style> 